import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowUpRight, Check, Clock3 } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { useSite } from "@/context/SiteContext";
import ServiceExtras from "@/components/ServiceExtras";
import api from "@/lib/api";
import { FALLBACK_SERVICES, HOW_IT_WORKS } from "@/lib/constants";

function FaqAccordion({ faqs }) {
  const [open, setOpen] = useState(null);
  if (!faqs.length) return null;
  return (
    <div className="divide-y divide-white/10 border border-white/10">
      {faqs.map((f, i) => (
        <div key={f._id || i}>
          <button
            onClick={() => setOpen(open === i ? null : i)}
            className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
            data-testid={`faq-toggle-${i}`}
          >
            <span className="font-display text-lg font-bold uppercase tracking-wide text-paper">{f.question}</span>
            <span className={`font-mono text-copper transition-transform duration-300 ${open === i ? "rotate-45" : ""}`}>+</span>
          </button>
          {open === i && (
            <p className="px-6 pb-6 text-sm leading-relaxed text-muted-foreground" data-testid={`faq-answer-${i}`}>{f.answer}</p>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const { openQuote } = useSite();
  const [service, setService] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/services/${slug}`)
      .then(({ data }) => setService(data))
      .catch(() => {
        const fallback = FALLBACK_SERVICES.find((s) => s.slug === slug);
        if (fallback) setService(fallback);
        else setNotFound(true);
      });
    api.get("/faqs").then(({ data }) => setFaqs((data || []).filter((f) => !f.service_slug || f.service_slug === slug))).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-6 bg-ink-950 px-6 text-center">
        <h1 className="display-title text-4xl">Serviciul nu a fost găsit</h1>
        <Link to="/servicii" className="btn-primary" data-testid="service-notfound-back">TOATE SERVICIILE</Link>
      </div>
    );
  }

  if (!service) {
    return <div className="min-h-[70vh] bg-ink-950" />;
  }

  const siblings = FALLBACK_SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <>
      <SEO title={service.seo_title || `${service.title} | BROX CONSTRUCT`} description={service.seo_description} path={`/servicii/${service.slug}`} image={service.hero_image}
        jsonLd={{
          "@context": "https://schema.org", "@type": "Service",
          name: service.title, description: service.seo_description || service.intro,
          provider: { "@type": "Organization", name: "BROX CONSTRUCT", url: "https://broxconstruct.ro" },
          areaServed: "România", url: `https://broxconstruct.ro/servicii/${service.slug}`,
        }}
      />

      <section className="grain relative flex min-h-[54vh] items-end overflow-hidden bg-ink-950 pt-24">
        <img src={service.hero_image} alt={`${service.title} — Brox Construct`} className="absolute inset-0 h-full w-full object-cover" data-testid="service-hero-image" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/70 to-ink-950/40" aria-hidden="true" />
        <div className="container-brox relative pb-14 pt-20">
          <Reveal>
            <nav className="eyebrow mb-5 flex items-center gap-2" aria-label=" breadcrumb ">
              <Link to="/servicii" className="transition-colors hover:text-paper" data-testid="service-breadcrumb">SERVICII</Link>
              <span aria-hidden="true">/</span>
              <span>{service.title}</span>
            </nav>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="display-title text-5xl sm:text-7xl" data-testid="service-title">{service.title}</h1>
            <p className="mt-4 max-w-xl text-base text-paper/80 sm:text-lg" data-testid="service-tagline">{service.tagline}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink-950 py-12 sm:py-24" data-testid="service-body-section">
        <div className="container-brox grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-lg leading-relaxed text-paper/90 sm:text-xl" data-testid="service-intro">{service.intro}</p>
            </Reveal>
            {service.description && (
              <Reveal delay={0.1}>
                <p className="mt-6 text-base leading-relaxed text-muted-foreground" data-testid="service-description">{service.description}</p>
              </Reveal>
            )}
            <Reveal delay={0.15}>
              <div className="mt-8 border border-copper/30 bg-copper/5 p-6 sm:p-9" data-testid="service-cta-panel">
                <h2 className="display-title text-3xl sm:text-4xl">Cere ofertă pentru {service.title.toLowerCase()}.</h2>
                <p className="mt-3 flex items-center gap-2 text-xs text-paper/70 sm:text-sm">
                  <Clock3 className="h-4 w-4 text-copper" />
                  Trimite-ne proiectul tău. Te contactăm în maximum 24 de ore.
                </p>
                <p className="mt-2 text-xs leading-relaxed text-paper/60 sm:text-sm">
                  Pentru unele lucrări este necesară o evaluare la fața locului înainte de oferta finală.
                </p>
                <button onClick={() => openQuote({ source: `service:${service.slug}` })} className="btn-primary copper-sheen mt-7 w-full sm:w-auto" data-testid="service-cta-whatsapp">
                  <WhatsAppIcon className="h-5 w-5" />
                  CERE OFERTĂ PE WHATSAPP
                </button>
              </div>
            </Reveal>
          </div>

          <aside className="lg:col-span-5">
            <Reveal delay={0.1}>
              <p className="label-brox">Ce includem</p>
              <ul className="grid gap-px border border-white/10 bg-white/10" data-testid="service-items">
                {service.items.map((item) => (
                  <li key={item} className="flex items-center gap-3 bg-ink-900 px-5 py-4 text-paper/85">
                    <Check className="h-4 w-4 shrink-0 text-copper" strokeWidth={3} />
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          </aside>
        </div>
      </section>

      <ServiceExtras slug={service.slug} />

      <section className="border-y border-white/10 bg-ink-900 py-12 sm:py-20" data-testid="service-process-section">
        <div className="container-brox">
          <Reveal><p className="eyebrow mb-3">CUM LUCRĂM</p></Reveal>
          <Reveal delay={0.05}><h2 className="display-title text-3xl sm:text-4xl">De la cerere la recepție</h2></Reveal>
          <ol className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {HOW_IT_WORKS.map((step, i) => (
              <Reveal key={step.nr} delay={0.06 * i}>
                <li className="border-t-2 border-copper/40 pt-4">
                  <span className="font-mono text-xs text-copper">{step.nr}</span>
                  <h3 className="mt-2 font-display text-lg font-bold uppercase tracking-wide text-paper">{step.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{step.text}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {faqs.length > 0 && (
        <section className="bg-ink-950 py-12 sm:py-24" data-testid="service-faq-section">
          <div className="container-brox max-w-3xl">
            <Reveal><p className="eyebrow mb-3">ÎNTREBĂRI FRECVENTE</p></Reveal>
            <Reveal delay={0.05}><h2 className="display-title mb-10 text-3xl sm:text-4xl">Răspunsuri utile</h2></Reveal>
            <FaqAccordion faqs={faqs} />
          </div>
        </section>
      )}

      <section className="border-t border-white/10 bg-ink-950 py-12 sm:py-20" data-testid="service-more-section">
        <div className="container-brox">
          <Reveal><p className="eyebrow mb-6">ALTE SERVICII</p></Reveal>
          <div className="grid gap-4 sm:grid-cols-3">
            {siblings.map((s) => (
              <Reveal key={s.slug}>
                <Link to={`/servicii/${s.slug}`} className="card-brox group flex items-center justify-between gap-4 p-6 hover:border-copper/50" data-testid={`service-more-${s.slug}`}>
                  <span className="display-title text-xl">{s.title}</span>
                  <ArrowUpRight className="h-5 w-5 shrink-0 text-copper" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
